import {
  AlertActiveSummary,
  AlertHistoryTraceResponse,
  AlertList,
  AlertTraceList,
  AlertType,
  AlertViewListResponse,
  AlertViewInfo,
  AlertViewAccountInfo,
} from "api/interfaces/alertInterface.interface";
import AlertViewPresenter from "./AlertViewPresenter";
import { useCallback, useEffect, useState } from "react";
import { useQuery } from "react-query";
import {
  getAlertTraceList,
  getAlertViewList,
  putAlertStatusAllClose,
} from "api/alertAPI";
import { ProfileAccountInfo } from "api/interfaces/accountInterface.interface";
import { useAppSelector } from "redux/hooks";
import { useMutation } from "react-query";
import { Option } from "utils/options";
import { useQueryClient } from "react-query";
import useApiError from "hook/useApiError";
import { CustomError } from "api/interfaces/commonInterface.interface";
import {
  useLocation,
  useNavigate,
  useOutletContext,
  useSearchParams,
} from "react-router-dom";
import { filteringDiskAlertBoolean } from "utils/AlertUtil";
import { selectMapStomp } from "redux/stomp/stompSelector";
import { useMenu } from "hook/useMenu";
import useAlertStatusCloseMutation from "hook/useAlertStatusCloseMutation";

const covertStringToObject = (string: string) => {
  return string
    .split("&")
    .reduce((acc: Record<string, string>, curr: string) => {
      const [key, value] = curr.split("=");
      acc[key] = value;
      return acc;
    }, {});
};

interface Props {
  isModal?: boolean;
  onModal?: () => void;
  searchParams?: URLSearchParams;
  setAccountInfo?: (accountInfo: AlertViewAccountInfo[]) => void;
}

const isEqualCategory = (category: string, alertType: string) => {
  return category.toUpperCase() === alertType.toUpperCase();
};

const AlertViewContainer = ({
  searchParams,
  onModal,
  isModal,
  setAccountInfo,
}: Props) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { handleErrorNotify, lang, notifyError, notifySuccess } = useApiError();

  const [urlSearchParams] = useSearchParams();
  const { menuNavigation } = useMenu({});
  const { onChangeTitle, onChangeStatusAlertSummary } =
    useOutletContext<any>() ?? {};

  const { mapLastUpdatedIdForAlertView, mapLastUpdatedAt } =
    useAppSelector(selectMapStomp);

  const recorderId =
    searchParams?.get("recorderId") ?? urlSearchParams.get("recorderId") ?? "";
  const historyId =
    searchParams?.get("historyId") ?? urlSearchParams.get("historyId") ?? "";
  const cameraId =
    searchParams?.get("cameraId") ?? urlSearchParams.get("cameraId") ?? "";
  const channel =
    searchParams?.get("channel") ?? urlSearchParams.get("channel") ?? "";
  const alertType =
    searchParams?.get("alertType")?.toUpperCase() ??
    urlSearchParams.get("alertType")?.toUpperCase() ??
    "";

  const from = location.state?.from;

  const queryClient = useQueryClient();
  const selectedAccount: ProfileAccountInfo = useAppSelector(
    (state) => state.accountSettings
  );

  const isMap = location.pathname.includes("map");
  const isHistoryOrModal = isModal
    ? true
    : location.pathname.includes("history");

  const [originList, setOriginList] = useState<AlertList[]>([]);

  const [alertInfo, setAlertInfo] = useState<AlertViewInfo | null>(null);
  const [traceList, setTraceList] = useState<AlertTraceList[]>([]);
  const [alertTypeList, setAlertTypeList] = useState<AlertType[]>([]);
  const [currentAlert, setCurrentAlert] = useState<AlertList | null>(null);
  const [count, setCount] = useState<AlertActiveSummary>({
    total: 0,
    systemCount: 0,
    diskCount: 0,
    videoCount: 0,
  });

  const [isConfirmStatus, SetIsConfirmStatus] = useState<boolean>(false);
  const [checkNoConfirm, setCheckNoConfirm] = useState<boolean>(false);

  const onCheckNoConfirm = (value: boolean) => {
    setCheckNoConfirm(value);
  };

  const onConfirmCancel = () => {
    SetIsConfirmStatus(false);
  };

  const onConfirmOk = useCallback(() => {
    if (location.pathname.includes("history")) {
      mutationAlertStatusClose.mutate({
        accountId: selectedAccount.accountId,
        recorderId: recorderId,
        historyId: historyId,
      });
      return;
    }
    mutationAlertStatusAllClose.mutate({
      accountId: selectedAccount.accountId,
      recorderId: recorderId,
    });
  }, [isHistoryOrModal, recorderId, selectedAccount.accountId, historyId]);

  const onFinishClose = useCallback(() => {
    if (isModal && onModal) {
      onModal();
      return;
    }
    if (from) {
      if (from.includes("?")) {
        const [path, queryString] = from.split("?");
        const queryParams: Record<string, string> =
          covertStringToObject(queryString);
        if (queryParams.recorderId) {
          queryParams.recorderId = recorderId;
        }
        menuNavigation({
          to: path,
          queryString: queryParams,
          replace: true,
        });
      } else {
        menuNavigation({
          to: from,
          replace: true,
        });
      }
    }
  }, [navigate, isModal, onModal]);

  const alertViewListQuery = useQuery(
    ["alertViewList", selectedAccount, recorderId, historyId, setAccountInfo],
    () =>
      getAlertViewList({
        accountId: selectedAccount.accountId,
        recorderId,
        ...(historyId && isHistoryOrModal && { historyId }),
      }),
    {
      retry: 0,
      enabled: !!recorderId,
      refetchOnWindowFocus: false,
      onSuccess: (res: AlertViewListResponse) => {
        // console.log(res.result, res.result.alerts, alertInfo);
        if (res.error !== 0 && !res.result) {
          setAlertTypeList([]);
          setCurrentAlert(null);
          return;
        }

        setCount(res.count);
        onChangeStatusAlertSummary && onChangeStatusAlertSummary(res.count);
        onChangeTitle && onChangeTitle(res.result.mergedSystemName);

        setAccountInfo?.(res.result?.accountInfo || []);

        if (!res.result.alerts) {
          setAlertTypeList([]);
          setCurrentAlert(null);
          return;
        }

        setAlertInfo((sel) => {
          return { ...sel, ...res.result };
        });
        setOriginList(res.result.alerts);
        let tempAlertTypeList = res.result.alerts
          .reduce((acc: AlertType[], cur: AlertList) => {
            if (
              (filteringDiskAlertBoolean(cur) && cur.storageName) ||
              (cur.type === "Video Loss" && cur.camera)
            ) {
              const index = acc.findIndex(
                (item: any) => item.type === cur.type
              );

              const newItem: AlertType = {
                ...cur,
                ...(filteringDiskAlertBoolean(cur) &&
                  cur.storageName && {
                    storageList: [
                      { value: cur.alertHistoryId, label: cur.storageName },
                    ],
                  }),
                ...(cur.type === "Video Loss" &&
                  cur.camera && {
                    cameraList: [
                      {
                        ...cur.camera,
                        value: cur.alertHistoryId,
                        label: cur.camera.cameraName,
                      },
                    ],
                  }),
              };
              // type 같은 것 끼리 이미 storageList 나 cameraList group 이 없을 경우 group 생성
              if (index !== -1) {
                if (cur.type === "Video Loss") {
                  acc[index] = {
                    ...acc[index],
                    ...newItem,
                    cameraList: [
                      ...(acc[index].cameraList || []),
                      ...(newItem.cameraList || []),
                    ],
                  };
                }
                if (filteringDiskAlertBoolean(cur)) {
                  acc[index] = {
                    ...acc[index],
                    ...newItem,
                    storageList: [
                      ...(acc[index].storageList || []),
                      ...(newItem.storageList || []),
                    ],
                  };
                }
                acc[index].cameraList?.sort((a: Option, b: Option) => {
                  return a.label.localeCompare(b.label, undefined, {
                    numeric: true,
                    sensitivity: "base",
                  });
                });
                acc[index].storageList?.sort((a: Option, b: Option) => {
                  return a.label.localeCompare(b.label, undefined, {
                    numeric: true,
                    sensitivity: "base",
                  });
                });
              } else {
                acc.push(newItem);
              }
            } else {
              acc.push({
                ...cur,
              });
            }
            return acc;
          }, [])
          .sort((a) => {
            // console.log(a);
            return isEqualCategory(a.category, alertType) ? -1 : 1;
          });
        setAlertTypeList(tempAlertTypeList);
        // 선택한 Alert 상태
        let tempCurrentAlert = res.result.alerts.find((alert, index) => {
          if (historyId && !isHistoryOrModal) {
            if (cameraId && channel) {
              return (
                cameraId === alert.camera?.cameraId &&
                Number(channel) === Number(alert.camera?.channel)
              );
            }
            return historyId === alert.alertHistoryId;
          }
          if (alertType) {
            return isEqualCategory(alert.category, alertType);
          }
          return index === 0;
        });

        setCurrentAlert(
          tempCurrentAlert ? tempCurrentAlert : res.result.alerts[0]
        );
      },
      onError: (err: any) => {
        setAlertTypeList([]);

        if (!isMap) {
          notifyError(lang.label.alert.notify.list.error);
        }
      },
    }
  );

  const alertTraceListQuery = useQuery(
    ["alertTraceList", selectedAccount, alertInfo],
    () =>
      getAlertTraceList({
        accountId: selectedAccount.accountId,
        recorderId,
        ...(historyId && isHistoryOrModal && { historyId }),
      }),
    {
      retry: 0,
      enabled: !!recorderId,
      refetchOnWindowFocus: false,
      onSuccess: (res: AlertHistoryTraceResponse) => {
        // console.log(res, "alert trace list");
        if (res.error !== 0 && res.result === undefined) {
          setTraceList([]);
        } else {
          setTraceList(
            res.result.map((item, index) => ({
              ...item,
              key: index,
            }))
          );
        }
      },
      onError: (err: any) => {
        setTraceList([]);

        if (!isMap) {
          notifyError(lang.label.alert.notify.list.error);
        }
      },
    }
  );
  const mutationAlertStatusClose = useAlertStatusCloseMutation({
    invalidateQueryKeys: ["alertTraceList"],
    onSuccess: () => {
      SetIsConfirmStatus(false);
      onFinishClose();
    },
  });

  const mutationAlertStatusAllClose = useMutation(putAlertStatusAllClose, {
    onSuccess: () => {
      onFinishClose();
      notifySuccess(lang.label.alert.notify.allClose.success);
      // queryClient.invalidateQueries("alertViewList");
      // queryClient.invalidateQueries("alertTraceList");
      queryClient.invalidateQueries("activeAlert");
      queryClient.invalidateQueries("alertHistoryList");
      queryClient.invalidateQueries("searchMapFilterResult");
      SetIsConfirmStatus(false);
    },
    onError: (err: CustomError) => {
      handleErrorNotify(err, lang.label.alert.notify.allClose.fail);
    },
  });

  const onCloseAllStatus = () => {
    SetIsConfirmStatus(true);
  };

  const onChangeCurrentAlert = useCallback(
    (alertType: AlertType, value?: string) => {
      // value가 들어올 때는 VideoLoss 나 Disk Alert 일 때 선택한 cameralist / storagelist 아이템 선택했을 떄임
      let type = alertType.type;

      const findItem: AlertList | undefined = originList.find(
        (origin) => origin.type === type && origin.alertHistoryId === value
      );
      // console.log(value, alertType, originList);

      if (value && findItem) {
        setCurrentAlert(findItem);
      } else {
        setCurrentAlert(originList.filter((origin) => origin.type === type)[0]);
      }
    },
    [currentAlert, originList, checkNoConfirm]
  );

  const updateAlertView = useCallback(() => {
    alertViewListQuery.refetch();
    alertTraceListQuery.refetch();
  }, [alertViewListQuery, alertTraceListQuery]);

  useEffect(() => {
    // console.log(mapLastUpdatedIdForAlertView, recorderId);
    if (
      mapLastUpdatedIdForAlertView &&
      mapLastUpdatedIdForAlertView === recorderId
    ) {
      updateAlertView();
    }
  }, [mapLastUpdatedAt, mapLastUpdatedIdForAlertView, recorderId]);

  return (
    <AlertViewPresenter
      alertViewRefetch={updateAlertView}
      alertInfo={alertInfo}
      currentAlert={currentAlert}
      alertTypeList={alertTypeList}
      traceList={traceList}
      count={count}
      isFetching={
        alertViewListQuery.isFetching || alertTraceListQuery.isFetching
      }
      recorderId={recorderId}
      onCloseAllStatus={onCloseAllStatus}
      isConfirmStatus={isConfirmStatus}
      onConfirmCancel={onConfirmCancel}
      onConfirmOk={onConfirmOk}
      onChangeCurrentAlert={onChangeCurrentAlert}
      onCheckNoConfirm={onCheckNoConfirm}
      checkNoConfirm={checkNoConfirm}
      onFinishClose={onFinishClose}
      isModal={isModal}
      historyId={historyId}
      onModal={onModal}
      isHistoryOrModal={isHistoryOrModal}
    />
  );
};

export default AlertViewContainer;
